import React from "react"
import ThemeProvider from "@material-ui/styles/ThemeProvider"

import Layout from "../../../components/layout"
import SEO from "../../../components/seo"
import PracticeRoutinePost from "../../../components/practice-routine-post"
import theme from "../../../theme"

const summary = [
  {
    level: "Preliminary to Grade 2",
    time: "15 to 25 minutes",
    focus: "Posture, bow hold, open strings, simple scales, short piece sections",
    weeklyHabit: "Practise most days, keep tasks short, and finish with rhythm or aural play",
  },
  {
    level: "Grades 3 to 4",
    time: "30 to 40 minutes",
    focus: "Intonation, bow control, shifting, string crossings, articulation",
    weeklyHabit: "Work in small goals and include sight reading two or three times a week",
  },
  {
    level: "Grades 5 to 6",
    time: "45 to 60 minutes",
    focus: "Technical accuracy, problem passages, metronome work, recorded listening",
    weeklyHabit: "Separate detailed practice from run-throughs and review tricky bars carefully",
  },
  {
    level: "Grades 7 to 8",
    time: "60 to 90 minutes",
    focus: "Stamina, interpretation, exam readiness, mock performances",
    weeklyHabit: "Rotate technical priorities and schedule accompaniment, sight reading, and rest",
  },
]

const sections = [
  {
    title: "Preliminary to Grade 2: build the habit",
    paragraphs: [
      "Aim for short, regular sessions of about 15 to 25 minutes. Start with posture, bow hold, open-string tone, and slow scales. Then spend most of the time on one small section of a piece rather than always playing from the beginning. Finish with clapping rhythms, singing intervals, or a few bars of simple sight reading.",
      "A good session might be five minutes of warm-up, five minutes of scales, ten minutes on pieces, and three minutes of listening or aural play. At this stage, consistency matters more than intensity.",
    ],
  },
  {
    title: "Grades 3 to 4: strengthen technique",
    paragraphs: [
      "Practice can grow to 30 to 40 minutes on most days. Keep scales and arpeggios unhurried, with careful attention to intonation and even bow speed. Add focused work on shifting, string crossings, articulation, and tone colours that appear in the pieces.",
      "Divide pieces into small goals: one passage for rhythm, one for tuning, one for phrasing, and one for memory or performance flow. Include sight reading two or three times a week so it becomes a normal skill, not a last-minute exam task.",
    ],
  },
  {
    title: "Grades 5 to 6: practise like a problem solver",
    paragraphs: [
      "At this level, 45 to 60 minutes is often more useful when broken into clear blocks. Spend the first block on technical work with a metronome and tuner, the second on difficult bars, and the third on larger musical runs. Record short sections and listen back for rhythm, tone, and shape.",
      "Plan one or two weekly run-throughs, but avoid turning every practice into a full performance. The best progress usually comes from isolating a tricky shift, bowing pattern, or phrase ending until it feels reliable.",
    ],
  },
  {
    title: "Grades 7 to 8: prepare the whole musician",
    paragraphs: [
      "Advanced AMEB preparation needs a mature balance of technical control, stamina, interpretation, and exam readiness. Sessions may run 60 to 90 minutes, but they should still be planned. Rotate technical priorities across the week, then give each major work both slow-detail practice and performance-tempo practice.",
      "Build in mock performances, accompaniment rehearsals, sight-reading practice, and aural revision. Leave time for recovery, too: tired hands and rushed repetition rarely produce secure playing.",
    ],
  },
  {
    title: "A simple weekly rhythm",
    paragraphs: [
      "Try using a shared calendar to assign each day a purpose: technique, pieces, sight reading, aural, recording, rehearsal, and rest. Students can see what matters today, and parents or teachers can support the routine without needing to rewrite the plan every week.",
      "The most useful routine is the one the student can actually repeat. Keep it visible, keep it specific, and adjust it whenever practice starts to feel mechanical instead of musical.",
    ],
  },
]

const ViolinPracticeRoutinesAmebLevels = () => (
  <ThemeProvider theme={theme}>
    <Layout>
      <SEO
        title="Violin Practice Schedule by AMEB Level"
        description="Plan violin practice for AMEB Preliminary through Grade 8 with level-appropriate routines for scales, pieces, sight reading, aural work, and performance readiness."
      />
      <PracticeRoutinePost
        instrument="Violin"
        title="Violin practice routines for different AMEB levels"
        intro="A strong AMEB routine is less about practising longer and more about giving every part of musicianship a regular place: sound, technique, pieces, sight reading, aural skills, and performance confidence."
        sharedCalendarUrl="https://app.schoolcalendar.me/calendar/cuddly-dragon-31"
        image="blog-violin.png"
        imageAlt="Violin practice planning"
        summary={summary}
        sections={sections}
      />
    </Layout>
  </ThemeProvider>
)

export default ViolinPracticeRoutinesAmebLevels
